import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import apiClient from "../../services/apiClient";

const SettlementViewPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [settlement, setSettlement] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);

    const fetchSettlement = async () => {
        try {
            setLoading(true);
            const response: any = await apiClient(`v1/resource/settlement/${id}`);
            setSettlement(response?.data?.settlements || null);
        } catch (err: any) {
            setError(err?.message || "Failed to load settlement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettlement();
    }, [id]);

    const markAsPaid = async () => {
        if (!settlement) return;
        try {
            setUpdating(true);
            await apiClient(`v1/resource/settlement/${settlement.id}`, {
                method: "PATCH",
                body: { status: "paid" },
            });
            setSettlement({ ...settlement, status: "paid" });
        } catch (err: any) {
            alert(err?.message || "Failed to update status");
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return (
        <Layout>
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading settlement details...</p>
                </div>
            </div>
        </Layout>
    );

    if (error) return (
        <Layout>
            <div className="flex items-center justify-center min-h-screen">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg max-w-md">
                    <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <p className="text-red-800 font-medium">{error}</p>
                    </div>
                </div>
            </div>
        </Layout>
    );

    if (!settlement) return (
        <Layout>
            <div className="flex items-center justify-center min-h-screen">
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg max-w-md">
                    <p className="text-yellow-800 font-medium">Settlement not found</p>
                </div>
            </div>
        </Layout>
    );

    const { provider, serviceProvider, total_amount, total_commission, payable_amount, status, settlement_date, items } = settlement;

    const formatDate = (dateStr: string) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const statusConfig: any = {
        paid: { bg: "bg-green-100", text: "text-green-800", border: "border-green-300", icon: "✓" },
        pending: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300", icon: "⏳" },
        failed: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300", icon: "✕" },
    };

    const currentStatus = statusConfig[status?.toLowerCase()] || { bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-300", icon: "•" };

    const InfoRow = ({ label, value, className = "" }: any) => (
        <div className={`flex flex-col ${className}`}>
            <span className="text-sm text-gray-500 mb-1">{label}</span>
            <span className="text-base font-medium text-gray-900">{value || "-"}</span>
        </div>
    );

    const renderUserInfo = (entity: any) => {
        const user = entity?.user;
        const info = entity?.additional_info || {};
        if (!user) return null;

        const isServiceProvider = entity.user?.role_id === 6;

        return (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-sm border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        {isServiceProvider ? "Service Provider Information" : "Provider Information"}
                    </h3>
                </div>

                <div className="flex gap-6 mb-6">
                    {(info.avatar_url || info.aadhar_image) && (
                        <div className="flex-shrink-0">
                            <img 
                                src={info.avatar_url || info.aadhar_image} 
                                alt="Profile" 
                                className="w-24 h-24 rounded-2xl border-4 border-white shadow-lg object-cover"
                            />
                        </div>
                    )}
                    <div className="flex-1">
                        <h4 className="text-2xl font-bold text-gray-900 mb-1">
                            {user.first_name} {user.last_name}
                        </h4>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                {user.email}
                            </span>
                            <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                {user.isd_code} {user.mobile}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {user.gender && <InfoRow label="Gender" value={user.gender} />}
                    {user.dob && <InfoRow label="Date of Birth" value={formatDate(user.dob)} />}
                    {user.blood_group && <InfoRow label="Blood Group" value={user.blood_group} />}
                    {info.qualification && <InfoRow label="Qualification" value={info.qualification} />}
                    {info.license_no && <InfoRow label="License Number" value={info.license_no} />}
                    {info.practising_since && <InfoRow label="Practising Since" value={formatDate(info.practising_since)} />}
                    {info.GSTIN && <InfoRow label="GSTIN" value={info.GSTIN} />}
                    {info.Business_hours && <InfoRow label="Business Hours" value={info.Business_hours} />}
                    {info.Off_days && <InfoRow label="Off Days" value={info.Off_days} />}
                </div>

                {entity.provider_speciality?.length > 0 && (
                    <div className="mb-4">
                        <span className="text-sm text-gray-500 mb-2 block">Specialties</span>
                        <div className="flex flex-wrap gap-2">
                            {entity.provider_speciality.map((s: any, idx: number) => (
                                <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                    {s.speciality}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {info.Service_location?.length > 0 && (
                    <div className="mb-4">
                        <span className="text-sm text-gray-500 mb-2 block">Service Locations</span>
                        <div className="flex flex-wrap gap-2">
                            {info.Service_location.map((l: any, idx: number) => (
                                <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                    📍 {l.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {info.bank_name && (
                    <div className="bg-white rounded-xl p-4 mb-4 border border-blue-200">
                        <span className="text-sm text-gray-500 mb-2 block">Bank Details</span>
                        <p className="font-medium text-gray-900">{info.bank_name}</p>
                        <p className="text-sm text-gray-600">Account: {info.bank_account_number}</p>
                    </div>
                )}

                {user.address && (
                    <div className="bg-white rounded-xl p-4 mb-4 border border-blue-200">
                        <span className="text-sm text-gray-500 mb-2 block">Address</span>
                        <p className="text-gray-900">
                            {user.address.address1} {user.address.address2 && `, ${user.address.address2}`}
                            <br />
                            {user.address.city}, {user.address.state}, {user.address.country} - {user.address.zipcode}
                        </p>
                    </div>
                )}

                {(info.aadhar_url || info.license_url || info.aadhar_image) && (
                    <div className="flex flex-wrap gap-3">
                        {info.aadhar_url && (
                            <a href={info.aadhar_url} target="_blank" rel="noopener noreferrer" 
                               className="px-4 py-2 bg-white border-2 border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm">
                                📄 View Aadhar
                            </a>
                        )}
                        {info.license_url && (
                            <a href={info.license_url} target="_blank" rel="noopener noreferrer"
                               className="px-4 py-2 bg-white border-2 border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm">
                                📋 View License
                            </a>
                        )}
                        {info.aadhar_image && (
                            <a href={info.aadhar_image} target="_blank" rel="noopener noreferrer"
                               className="px-4 py-2 bg-white border-2 border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm">
                                🖼️ View Aadhar Image
                            </a>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 py-8">
                <div className="mb-6 flex items-center justify-between">
                    <button 
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 hover:border-gray-400 rounded-xl font-semibold transition-all shadow-sm hover:shadow" 
                        onClick={() => navigate(-1)}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </button>
                    
                    <div className={`flex items-center gap-2 px-4 py-2 ${currentStatus.bg} ${currentStatus.text} border-2 ${currentStatus.border} rounded-xl font-bold text-lg`}>
                        <span className="text-xl">{currentStatus.icon}</span>
                        {status.toUpperCase()}
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-gray-100">
                    <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span className="w-1.5 h-8 bg-blue-500 rounded-full"></span>
                        Settlement Details
                    </h1>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border-2 border-blue-200">
                            <div className="text-sm text-blue-600 font-semibold mb-2">Total Amount</div>
                            <div className="text-3xl font-bold text-blue-900">{formatCurrency(total_amount)}</div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border-2 border-orange-200">
                            <div className="text-sm text-orange-600 font-semibold mb-2">Commission</div>
                            <div className="text-3xl font-bold text-orange-900">{formatCurrency(total_commission)}</div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-2 border-green-200">
                            <div className="text-sm text-green-600 font-semibold mb-2">Payable Amount</div>
                            <div className="text-3xl font-bold text-green-900">{formatCurrency(payable_amount)}</div>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 mb-8 p-4 bg-gray-50 rounded-xl">
                        <svg className="w-6 h-6 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <div className="text-sm text-gray-500">Settlement Date</div>
                            <div className="text-lg font-bold text-gray-900">{formatDate(settlement_date)}</div>
                        </div>
                    </div>

                    {status.toLowerCase() !== "paid" && (
                        <button
                            onClick={markAsPaid}
                            disabled={updating}
                            className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 disabled:from-gray-300 disabled:to-gray-400 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                        >
                            {updating ? (
                                <span className="flex items-center gap-2">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Processing...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Mark as Paid
                                </span>
                            )}
                        </button>
                    )}
                </div>

                {(provider || serviceProvider) && (
                    <div className="mb-6">
                        {provider ? renderUserInfo(provider) : renderUserInfo(serviceProvider)}
                    </div>
                )}

                {items?.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                            <span className="w-1.5 h-8 bg-purple-500 rounded-full"></span>
                            Settlement Items
                            <span className="ml-auto text-sm font-normal text-gray-500">
                                {items.length} {items.length === 1 ? 'item' : 'items'}
                            </span>
                        </h3>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Item ID</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Amount</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Commission</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Payable</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {items.map((item: any, index: number) => (
                                        <tr key={item.id} className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg font-mono text-sm">
                                                    #{item.id}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-gray-900">
                                                {formatCurrency(item.total_amount)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-orange-600">
                                                {formatCurrency(item.total_commission)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-green-600">
                                                {formatCurrency(item.payable_amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gradient-to-r from-blue-50 to-blue-100 border-t-2 border-blue-200 font-bold">
                                        <td className="px-6 py-4 text-gray-700">TOTAL</td>
                                        <td className="px-6 py-4 text-right text-gray-900">{formatCurrency(total_amount)}</td>
                                        <td className="px-6 py-4 text-right text-orange-700">{formatCurrency(total_commission)}</td>
                                        <td className="px-6 py-4 text-right text-green-700">{formatCurrency(payable_amount)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SettlementViewPage;